import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import type { Warehouse } from './types'

interface KPI {
  warehouse_id: string
  planned: number
  started: number
  completed: number
}

interface TodayRow {
  devanningId: string
  warehouseId: string
  warehouseCode: string
  plannedDevanAt: string
  containerNo: string
  jobRef: string | null
  customerName: string | null
  ctns: number
  status: string
}

const STATUS_COLORS: Record<string, string> = {
  planned:        'bg-blue-100 text-blue-700',
  started:        'bg-yellow-100 text-yellow-700',
  completed:      'bg-green-100 text-green-700',
  cancelled:      'bg-gray-100 text-gray-500',
  pre_devanning:  'bg-purple-100 text-purple-700',
}

const STATUS_LABELS: Record<string, string> = {
  planned:       'Planned',
  started:       'In Progress',
  completed:     'Completed',
  cancelled:     'Cancelled',
  pre_devanning: 'Pre-Devanning',
}


const PLAN_SELECT = `
  id, container_no, job_ref, source_loading_list_id,
  plan_items:devanning_plan_items(expected_cartons),
  devannings(id, status),
  ll:loading_lists!source_loading_list_id(
    job_ref,
    customer:customers!source_customer_id(name)
  )
`

export default function WarehouseList() {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [kpis,       setKpis]       = useState<Record<string, KPI>>({})
  const [todayRows,  setTodayRows]  = useState<TodayRow[]>([])
  const [loading,         setLoading]         = useState(true)
  const [scheduleLoading, setScheduleLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setScheduleLoading(true)
      try {
        const [
          { data: whs },
          { data: allSched },
          { data: activeDevs },
          { data: todaySched },
        ] = await Promise.all([
          supabase.from('warehouses').select('*').eq('is_active', true).order('name'),
          supabase.from('devanning_schedule').select('warehouse_id').not('warehouse_id', 'is', null),
          supabase.from('devannings')
            .select('id, status, devanning_plans!inner(warehouse_id)')
            .in('status', ['started', 'completed']),
          supabase.from('today_devanning_schedule')
            .select('id, job_ref, warehouse_id, container_no, planned_devan_at')
            .order('planned_devan_at', { ascending: true }),
        ])

        const whList = (whs as Warehouse[]) || []
        setWarehouses(whList)

        // ── KPI counts ──────────────────────────────────────────────────────────
        const kpiMap: Record<string, KPI> = {}
        for (const w of whList) kpiMap[w.id] = { warehouse_id: w.id, planned: 0, started: 0, completed: 0 }

        // Planned = all schedule entries for this warehouse (includes Pre-Devanning rows)
        for (const s of (allSched || []) as any[]) {
          if (s.warehouse_id && kpiMap[s.warehouse_id]) kpiMap[s.warehouse_id].planned++
        }
        // In Progress / Completed from actual devannings
        for (const d of (activeDevs || []) as any[]) {
          const plan = Array.isArray(d.devanning_plans) ? d.devanning_plans[0] : d.devanning_plans
          const wid  = plan?.warehouse_id
          if (wid && kpiMap[wid]) {
            if (d.status === 'started')   kpiMap[wid].started++
            if (d.status === 'completed') kpiMap[wid].completed++
          }
        }
        setKpis(kpiMap)
        setLoading(false)

        // ── Today's schedule rows ────────────────────────────────────────────────
        const whMap: Record<string, Warehouse> = {}
        for (const w of whList) whMap[w.id] = w

        const todayEntries = (todaySched || []) as any[]
        if (todayEntries.length === 0) {
          setTodayRows([])
          return
        }

        // Fetch plans for matching job_refs (same dual-path lookup as Schedule page)
        const jobRefs = todayEntries.map(s => s.job_ref).filter(Boolean) as string[]
        const planMap: Record<string, any> = {}
        if (jobRefs.length > 0) {
          const { data: plansA } = await supabase.from('devanning_plans').select(PLAN_SELECT).in('job_ref', jobRefs)
          const { data: llData } = await supabase.from('loading_lists').select('id, job_ref').in('job_ref', jobRefs)
          const llIds = ((llData ?? []) as any[]).map(ll => ll.id as string)
          const { data: plansB } = llIds.length > 0
            ? await supabase.from('devanning_plans').select(PLAN_SELECT).in('source_loading_list_id', llIds).is('job_ref', null)
            : { data: [] }
          for (const p of [...(plansA ?? []), ...(plansB ?? [])] as any[]) {
            const llJobRef = Array.isArray(p.ll) ? p.ll[0]?.job_ref : p.ll?.job_ref
            planMap[p.job_ref ?? llJobRef] = p
          }
        }

        const rows: TodayRow[] = todayEntries.map(sched => {
          const plan = sched.job_ref ? planMap[sched.job_ref] : null
          const devs: any[] = plan?.devannings ?? []
          const dev  = devs.find(d => d.status !== 'cancelled') ?? devs[0]
          const ll   = plan ? (Array.isArray(plan.ll) ? plan.ll[0] : plan.ll) : null
          const ctns = ((plan?.plan_items ?? []) as any[]).reduce((s: number, it: any) => s + (it.expected_cartons ?? 0), 0)
          const wh   = whMap[sched.warehouse_id]
          return {
            devanningId:    dev?.id ?? sched.id,
            warehouseId:    sched.warehouse_id ?? '',
            warehouseCode:  wh?.code ?? '?',
            plannedDevanAt: sched.planned_devan_at,
            containerNo:    sched.container_no ?? plan?.container_no ?? '—',
            jobRef:         sched.job_ref ?? null,
            customerName:   ll?.customer?.name ?? null,
            ctns,
            status: dev?.status ?? 'pre_devanning',
          }
        })
        setTodayRows(rows)
      } catch (e) {
        console.error('WarehouseList load error:', e)
        setLoading(false)
        setTodayRows([])
      } finally {
        setScheduleLoading(false)
      }
    }
    load()
  }, [])

  const displayDate = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Warehouses</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Devanning, loading lists, and delivery notes</p>
          </div>
          <Link to="/warehouse/new"
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            + New Warehouse
          </Link>
        </div>

        {/* Today's Schedule */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">Today's Schedule</h2>
              <p className="text-xs text-gray-400 mt-0.5">{displayDate}</p>
            </div>
            <Link to="/warehouse/schedule" className="text-sm text-blue-600 hover:underline font-medium">
              Full Schedule →
            </Link>
          </div>
          {scheduleLoading ? (
            <div className="px-6 py-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : todayRows.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-400 text-sm">
              No devannings scheduled for today.
              <Link to="/warehouse/schedule" className="ml-1 text-blue-600 hover:underline">View full schedule →</Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm divide-y divide-gray-100 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Time', 'Job Ref', 'Customer', 'Container', 'CTNS', 'Warehouse', 'Status'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {todayRows.map(r => (
                    <tr key={r.devanningId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-500">
                        {new Date(r.plannedDevanAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-blue-700 dark:text-blue-400">{r.jobRef || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 text-xs max-w-[160px] truncate">{r.customerName || '—'}</td>
                      <td className="px-4 py-2.5 font-mono text-xs font-semibold text-gray-800 dark:text-gray-100">{r.containerNo}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{r.ctns || '—'}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded font-mono">{r.warehouseCode}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-500'}`}>
                          {STATUS_LABELS[r.status] ?? r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Warehouse cards */}
        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {warehouses.map(wh => {
              const kpi = kpis[wh.id] || { planned: 0, started: 0, completed: 0 }
              return (
                <Link key={wh.id} to={`/warehouse/${wh.id}`}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-xs font-mono font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">{wh.code}</span>
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-2">{wh.name}</h2>
                    </div>
                    <span className="text-xs text-green-600 font-medium">Active</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {[
                      { label: 'Planned',     val: kpi.planned,   color: 'text-gray-600' },
                      { label: 'In Progress', val: kpi.started,   color: 'text-yellow-600' },
                      { label: 'Completed',   val: kpi.completed, color: 'text-green-600' },
                    ].map(({ label, val, color }) => (
                      <div key={label} className="text-center">
                        <p className={`text-2xl font-bold ${color}`}>{val}</p>
                        <p className="text-xs text-gray-400">{label}</p>
                      </div>
                    ))}
                  </div>
                </Link>
              )
            })}
            {warehouses.length === 0 && (
              <div className="col-span-3 text-center py-20 text-gray-400">No warehouses yet.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
